/**
 * AI 생성 진입 카드 안의 그림 — 주제 카드 입력칸, 자료 카드 로고 줄.
 * 순수 장식이라 aria-hidden 이다. 카드 hover 면 그림이 4px 위로 뜬다(PRD 3-6 공통).
 * 그림은 카드 머리 아래 남은 칸(그림 칸)에 놓이고 오른쪽 끝에서 잘린다(카드가 clip).
 */
import { useId } from 'react';
import { Mint600, Violet300 } from '@zep-us/zus/tokens';
import { SparklingFillIcon } from '@zep-us/zus-icons';
import { cn } from '@/utils/tailwind.util';
import { BUILD_ENTRY_PICTURE_HOVER_CLASS, type BuildEntryCardSize } from '../../../components/BuildEntryCard';
import { useTopicSampleTyping } from '../hooks/useTopicSampleTyping';
import { AiBuilderAttachmentExtensionIcon } from './AiBuilderAttachmentExtensionIcon';

/**
 * 주제 카드 입력칸 그림 — 그림 칸 세로 가운데(+2px), 왼쪽 97 에서 시작해 폭 560 으로 카드 밖에서 잘린다.
 * 위치는 바깥이, hover 4px 는 안쪽이 맡는다 — 둘 다 translate 라 한 요소에 두면 겹쳐 쓴다.
 */
/** 진입 카드 크기 — 작게(small)는 tablet-sm 이상에서만 작고 그 아래는 크게 그린다(카드와 같다). */
type EntryPictureSize = BuildEntryCardSize;

/** Figma CardArt size=sm / lg 값 — small 은 모바일에서 lg, tablet-sm 이상에서 sm. */
const TOPIC_INPUT_CLASS: Record<EntryPictureSize, string> = {
  small: 'h-[48px] text-body-lg-medium tablet-sm:h-[44px] tablet-sm:text-body-md-medium',
  large: 'h-[48px] text-body-lg-medium'
};
/** ✨ 는 큰 카드(lg, 모바일 포함) 18 · 작은 카드(sm) 16 (Figma CardArt topic). */
const TOPIC_ICON_CLASS: Record<EntryPictureSize, string> = {
  small: 'size-[18px] tablet-sm:size-[16px]',
  large: 'size-[18px]'
};
/** 커서 높이 — sm 20 · lg 22. */
const TOPIC_CARET_CLASS: Record<EntryPictureSize, string> = {
  small: 'h-[22px] tablet-sm:h-[20px]',
  large: 'h-[22px]'
};
/** 로고 칸 48(sm 44) 안 로고 27(sm 25) — 로고 아이콘 크기는 칸이 정한다. */
const LOGO_TILE_CLASS: Record<EntryPictureSize, string> = {
  small: 'size-[48px] [&>svg]:size-[27px] tablet-sm:size-[44px] tablet-sm:[&>svg]:size-[25px]',
  large: 'size-[48px] [&>svg]:size-[27px]'
};
export const TopicInputPicture = ({
  samples,
  size
}: {
  /** 키보드로 쓰고 지우듯 돌아가며 보여 줄 예시(M7). 첫 예시가 reduced-motion 때 멈춰 보이는 대표 예시다. */
  samples: readonly string[];
  size: EntryPictureSize;
}) => {
  const sparkleGradientId = `ai-entry-sparkle-${useId().replace(/:/g, '')}`;
  const {
    ref,
    text
  } = useTopicSampleTyping(samples);
  return (
    // 바깥은 그림 칸 폭을 재는 컨테이너(cqw) — 예시가 길어도 커서가 카드 안에 남도록 글자 칸 폭을 정한다.
    <div ref={ref} aria-hidden className="absolute inset-x-0 top-[calc(50%+2px)] -translate-y-1/2 [container-type:inline-size]" data-sentry-component="TopicInputPicture" data-sentry-source-file="AiGenerateEntryPictures.tsx">
      <div className={cn('relative ml-[97px] w-[560px]', BUILD_ENTRY_PICTURE_HOVER_CLASS)}>
        {/* Figma 뒤 빛 — 입력칸 뒤 560×80 타원(왼쪽으로 20 · 위아래 16 넘침), rgba(115,128,255,0.1) · blur 10. */}
        <div className="absolute left-[-20px] top-1/2 h-4xl w-[560px] -translate-y-1/2 rounded-[50%] bg-[rgb(115_128_255/0.1)] blur-[10px]" />
        <div className={cn('relative flex items-center gap-xs rounded-lg border border-violet-100 bg-default pl-sm text-default', TOPIC_INPUT_CLASS[size])}>
          {/* Figma ✨ — violet-300 → mint-600 그라디언트. */}
          <svg width="0" height="0" className="absolute" data-sentry-element="svg" data-sentry-source-file="AiGenerateEntryPictures.tsx">
            <defs data-sentry-element="defs" data-sentry-source-file="AiGenerateEntryPictures.tsx">
              <linearGradient id={sparkleGradientId} x1="0" y1="0" x2="1" y2="0.16" data-sentry-element="linearGradient" data-sentry-source-file="AiGenerateEntryPictures.tsx">
                <stop stopColor={Violet300} data-sentry-element="stop" data-sentry-source-file="AiGenerateEntryPictures.tsx" />
                <stop offset="1" stopColor={Mint600} data-sentry-element="stop" data-sentry-source-file="AiGenerateEntryPictures.tsx" />
              </linearGradient>
            </defs>
          </svg>
          <SparklingFillIcon fill={`url(#${sparkleGradientId})`} className={TOPIC_ICON_CLASS[size]} data-sentry-element="SparklingFillIcon" data-sentry-source-file="AiGenerateEntryPictures.tsx" />
          {/* 글자 뒤 2 에 커서(폭 1.5 · 모서리 4)가 깜빡인다 — 「여기에 쓰면 된다」는 신호.
              번역이 길어 카드 끝(16 앞)을 넘으면 실제 입력칸처럼 앞 글자가 왼쪽으로 밀려 나간다(오른쪽 정렬 + 잘림).
              152 = 입력칸 시작 97 + 테두리 1 + 왼쪽 여백 12 + ✨ 18 + 간격 8 + 카드 끝 여백 16. */}
          <span className="flex max-w-[calc(100cqw-152px)] items-center justify-end gap-4xs overflow-hidden">
            <span className="shrink-0 whitespace-pre">{text}</span>
            <span className={cn('w-[1.5px] shrink-0 rounded-2xs bg-violet-500 animate-ai-entry-caret-blink motion-reduce:animate-none', TOPIC_CARET_CLASS[size])} />
          </span>
        </div>
      </div>
    </div>
  );
};

/**
 * 자료 카드 로고 — 지금 받을 수 있는 파일 확장자(ALLOWED_AI_FILE_EXTENSIONS)에 등록된 아이콘만 쓴다.
 * 아이콘은 첨부 칩과 같은 매핑(AiBuilderAttachmentExtensionIcon)이라 확장자가 바뀌면 함께 바뀐다.
 * csv·xlsx, hwp·hwpx 는 아이콘이 같아 하나씩만 둔다. 이미지(png·jpg·jpeg)는 받지만 그림에선 숨긴다.
 */
const SOURCE_LOGO_EXTENSIONS = ['pdf', 'pptx', 'docx', 'xlsx', 'hwp', 'txt', 'md'] as const;
const LogoTiles = ({
  size
}: {
  size: EntryPictureSize;
}) => <div className="flex shrink-0 gap-sm pr-sm" data-sentry-component="LogoTiles" data-sentry-source-file="AiGenerateEntryPictures.tsx">
    {SOURCE_LOGO_EXTENSIONS.map(extension => <div key={extension} className={cn('flex shrink-0 items-center justify-center rounded-md border border-assistive bg-default shadow-[0_2px_6px_rgb(26_26_64/0.08)]', LOGO_TILE_CLASS[size])}>
        <AiBuilderAttachmentExtensionIcon extension={extension} />
      </div>)}
  </div>;

/**
 * M2 — 로고 줄이 오른쪽→왼쪽으로 20s 한 바퀴 무한 롤링. hover 면 멈추고, 카드 양 끝 57 은 흐려진다.
 * 같은 줄을 두 번 이어 붙여 -50% 까지 옮기면 이음매 없이 돈다 — 줄 끝 여백(pr-sm)을 칸 사이 간격(gap-sm)과
 * 같게 둬야 이어 붙인 자리 간격이 같아 끊겨 보이지 않는다. 주제 카드 입력칸과 같은 줄 높이(세로 가운데 +2)·
 * 왼쪽 97 에서 시작한다(Figma CardArt type=source). 위치(바깥)와 hover 4px(안쪽)는 둘 다 translate 라 요소를 나눈다.
 */
export const SourceLogoRollPicture = ({
  size
}: {
  size: EntryPictureSize;
}) => <div aria-hidden className="absolute inset-x-0 top-[calc(50%+2px)] -translate-y-1/2" data-sentry-component="SourceLogoRollPicture" data-sentry-source-file="AiGenerateEntryPictures.tsx">
    <div className={cn('overflow-hidden py-2xs pl-[97px] [mask-image:linear-gradient(to_right,transparent,black_57px,black_calc(100%-57px),transparent)]', BUILD_ENTRY_PICTURE_HOVER_CLASS)}>
      <div className="flex w-max animate-ai-entry-logo-roll group-hover:[animation-play-state:paused] motion-reduce:animate-none">
        <LogoTiles size={size} data-sentry-element="LogoTiles" data-sentry-source-file="AiGenerateEntryPictures.tsx" />
        <LogoTiles size={size} data-sentry-element="LogoTiles" data-sentry-source-file="AiGenerateEntryPictures.tsx" />
      </div>
    </div>
  </div>;